import { useState, useMemo, useEffect, useRef } from "react";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Users,
    Calendar,
    Shield,
    Tags,
    UserCheck,
    Edit,
    Trash2,
    MoreHorizontal,
    X,
    Monitor,
    CheckCircle,
    AlertTriangle
} from "lucide-react";
import { FaAws, FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { SiGooglecloud } from "react-icons/si";
import { Cloud } from "lucide-react";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useToast } from "../../../hooks/use-toast";
import { myLocalStorage } from "../../StorageHelper";
import { getAdminUsers, getUsers } from "../UserRoles/Api/userrolesapi";
import { RoleCategory, RoleUser, UserInRoles } from "../UserRoles/interface";
import { Device, Tag } from "../interfaces/EndpointInterfaces";
import { Role } from "../InviteUsers/interface";
import { fetchAllRoles, fetchAllDevices } from "../InviteUsers/Api/inviteuserdetails";
import { searchTagsForPicker, fetchAllTagsForTenant } from "../Tags/Api/tagdetailsapi";
import { deleteUser, editRegisterUser } from "./Api/registeredUserApi";
import EmptyStateCard from "../EmptyStateCard";
import useUserStore from "../../../services/userStore";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
} from "../../../utils/listPaginationState";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { fetchAllComputers } from "../GatewayDevices/Api/gatewaydevicesapi";


const ITEMS_PER_PAGE = 10;

export default function RegisteredUserDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.registeredUsers);
    const skipTabPageResetRef = useRef(true);
    const [searchTerm, setSearchTerm] = useState(parsePersistedString(savedState.searchTerm));
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const [activeTab, setActiveTab] = useState(parsePersistedString(savedState.activeTab, "admin"));
    const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<RoleUser | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<RoleUser | null>(null);
    const [editRole, setEditRole] = useState("");
    const [editSelectedTags, setEditSelectedTags] = useState<Tag[]>([]);
    const [editRoles, setEditRoles] = useState<string[]>([]);
    const [editMachines, setEditMachines] = useState<string[]>([]);
    const [originalRole, setOriginalRole] = useState("");
    const [originalRoles, setOriginalRoles] = useState<string[]>([]);
    const [originalMachines, setOriginalMachines] = useState<string[]>([]);
    const [tagSearch, setTagSearch] = useState("");
    const [tagPickerPage, setTagPickerPage] = useState(1);
    const [tagPickerTotalPages, setTagPickerTotalPages] = useState(1);
    const [isTagSearchLoading, setIsTagSearchLoading] = useState(false);
    const [roleSearch, setRoleSearch] = useState("");
    const [machineSearch, setMachineSearch] = useState("");
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
    const [userInRoles, setUserInRoles] = useState<UserInRoles>();
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    const [adminRoles, setAdminRoles] = useState<Role[] | []>([])
    const [userRoles, setUserRoles] = useState<Role[] | []>([])
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    let { email, role, userRole } = useUserStore((state) => state.user);


    const getAllDevices = async () => {
        const devices: Device[] = await fetchAllDevices(tenantName);
        setAllDevices(devices);
    }

    const filteredUsers = useMemo(() => {
        setLoading(true)
        if (!userInRoles) {
            setLoading(false)
            return []
        };

        const type = activeTab === "admin" ? "admin" : "users";
        const roleCategoryForTab: RoleCategory = userInRoles.roles[type] || {};

        let allUsers: RoleUser[] = Object.values(roleCategoryForTab).flat();

        const uniqueUsersMap = new Map<string, RoleUser>();
        for (const user of allUsers) {
            const email = user.email;
            const roleName = user.roleName?.trim();

            if (roleName === "super-user") continue;

            const existing = uniqueUsersMap.get(email);

            if (!existing) {
                uniqueUsersMap.set(email, { ...user, roleName: roleName || "" });
            } else {
                const currentRoles = existing.roleName
                    ? existing.roleName.split(",").map((r) => r.trim())
                    : [];

                const newRoles = roleName ? [roleName] : [];

                const mergedRoles = Array.from(new Set([...currentRoles, ...newRoles]));

                existing.roleName = mergedRoles.join(", ");
            }
        }


        allUsers = Array.from(uniqueUsersMap.values()).sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
        });

        if (showUnassignedOnly) {
            allUsers = allUsers.filter((user) => user.roleName === "unassigned-user");
        }
        setLoading(false)
        if (!searchTerm) return allUsers;
        const searchLower = searchTerm.toLowerCase();

        return allUsers.filter((user) => {
            const firstName = user.firstName?.toLowerCase() ?? "";
            const lastName = user.lastName?.toLowerCase() ?? "";
            const fullName = `${firstName} ${lastName}`.trim();
            return (
                firstName.includes(searchLower) ||
                lastName.includes(searchLower) ||
                fullName.includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.roleName?.toLowerCase().includes(searchLower) ||
                (user.tagsList &&
                    user.tagsList.some((tag) =>
                        tag?.toLowerCase().includes(searchLower)
                    ))
            );
        });
    }, [userInRoles, activeTab, showUnassignedOnly, searchTerm]);

    const paginatedUsers = useMemo(() => {

        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleEdit = async (user: RoleUser) => {
        setUserToEdit(user);
        setTagPickerPage(1);
        setTagSearch("");
        if (activeTab === "admin") {
            const role = user.roleName || "";
            setEditRole(role);
            setOriginalRole(role);

            const allTags = await fetchAllTagsForTenant(tenantName);
            const selectedTags = (user.tagsList || [])
                .map((tagName) => allTags.find((tag) => tag.name === tagName))
                .filter((tag): tag is Tag => Boolean(tag));
            setEditSelectedTags(selectedTags);
        } else {
            const roles = user.roleName
                ? user.roleName.split(",").map((r) => r.trim()).filter(Boolean)
                : [];
            const machines = (user.adComputerList || []).map(
                m => m.dNSHostName || m.name || m.id
            );
            setEditRoles(roles);
            setEditMachines(machines);

            setOriginalRoles([...roles]);
            setOriginalMachines([...machines]);
        }
        getAllDevices();
        await loadTagsForPicker(1, "");
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!userToEdit) return;

        let payload: any[] = [];
        let type = activeTab === "admin" ? "admin" : "users";
        let selectedTagIds: any[] = [];
        if (type === "admin") {
            if (!editRole?.trim()) return;
            const selectedRole = adminRoles.find((r) => r.roleName === editRole);
            selectedTagIds = editSelectedTags.map((tag) => String(tag.id));

            if (selectedRole) {
                payload = [{ customRole: false, roleType: "admin", roleId: selectedRole.roleId, roleName: selectedRole.roleName, tenantName, tags: selectedTagIds, selectedRole }];
            }
        } else {
            if (editRoles.length === 0) return;
            const roleCategoryForTab: RoleCategory = userInRoles?.roles[type] || {};
            const rolesForSave =
                editRoles.length > 1
                    ? editRoles.filter((roleName) => roleName !== "unassigned-user")
                    : editRoles;

            payload = rolesForSave.map((roleName) => {
                let roleId = 0;
                Object.values(roleCategoryForTab).forEach((roleArr) => {
                    roleArr.forEach((r) => {
                        if (r.roleName === roleName) {
                            roleId = r.roleId;
                        }
                    });
                });
                return { customRole: false, roleType: "users", roleId, roleName, tenantName };
            });
        }
        let userEmail = "";
        if (!email.startsWith("wsadmin")) {
            userEmail = email;
        }
        const devices: any[] = await fetchAllDevices(tenantName);
        const selectedMachineIds = editMachines
            .map(name => {
                const device = devices.find(
                    d => d.dNSHostName?.trim().toLowerCase() === name.trim().toLowerCase()
                );
                return device?.id;
            })
            .filter(Boolean);

        await editRegisterUser(payload, type, userToEdit.email, selectedMachineIds, tenantName, selectedTagIds || []);

        toast({
            title: "User Updated",
            description: `${userToEdit.firstName} ${userToEdit.lastName} has been updated successfully.`,
        });


        setEditDialogOpen(false);
        setUserToEdit(null);
        setEditRole("");
        setEditSelectedTags([]);
        setEditRoles([]);
        setEditMachines([]);
        setTagSearch("");
        setRoleSearch("");
        setMachineSearch("");
        getRegisteredUser();
    };


    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setUserToEdit(null);
        setEditRole("");
        setEditSelectedTags([]);
        setEditRoles([]);
        setEditMachines([]);
        setOriginalRole("");
        setOriginalRoles([]);
        setOriginalMachines([]);
        setTagSearch("");
        setRoleSearch("");
        setMachineSearch("");
    };

    const handleTagSelect = (tagName: string) => {
        setEditSelectedTags((prev) => {
            const existing = prev.find((item) => item.name === tagName);
            if (existing) {
                return prev.filter((item) => item.name !== tagName);
            }
            const tag = availableTags.find((item) => item.name === tagName);
            return tag ? [...prev, tag] : prev;
        });
    };

    const handleTagRemove = (tagId: number) => {
        setEditSelectedTags((prev) => prev.filter((item) => item.id !== tagId));
    };

    const handleRoleSelect = (role: string) => {
        setEditRoles(prev => {
            if (role === "epam-user") {
                if (prev.includes("epam-user")) {
                    return prev.filter(r => r !== "epam-user");
                } else {
                    return [role];
                }
            } else {
                const filtered = prev.filter(r => r !== "epam-user");
                if (filtered.includes(role)) {
                    return filtered.filter(r => r !== role);
                }
                const next = [...filtered, role];
                if (role !== "unassigned-user") {
                    return next.filter(r => r !== "unassigned-user");
                }
                return next;
            }
        });
    };

    const handleMachineSelect = (machineId: string) => {
        setEditMachines(prev => {
            if (prev.includes(machineId)) {
                return prev.filter(m => m !== machineId);
            } else {
                return [...prev, machineId];
            }
        });
    };

    const handleDeleteClick = (user: RoleUser) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (userToDelete) {
            let realms = [];
            if (activeTab === "admin")
                realms = ["whiteswan_tenants", "whiteswan_tenants_legacy"]
            else {
                let realmpasskey = "";
                let realmLegacy = "";
                const realm = tenantName;

                if (realm.endsWith(".local")) {
                    realmpasskey = realm;
                    realmLegacy = realm.replace(".local", "-legacy.local");
                } else if (realm.endsWith("-legacy.local")) {
                    realmpasskey = realm.replace("-legacy.local", ".local");
                    realmLegacy = realm;
                } else if (realm.endsWith("_Legacy")) {
                    realmpasskey = realm.replace("_Legacy", "");
                    realmLegacy = realm;
                } else {
                    realmpasskey = realm;
                    realmLegacy = `${realm}_Legacy`;
                }
                realms = [realmpasskey, realmLegacy]
            }

            await deleteUser(userToDelete.email, realms);
            toast({
                title: "User Deleted",
                description: `${userToDelete.firstName} ${userToDelete.lastName} has been removed from the system.`,
                variant: "destructive",
            });
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            getRegisteredUser();
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const filteredTags = availableTags;

    const handleTagSearch = async () => {
        await loadTagsForPicker(1, tagSearch);
    };

    const filteredRoles = activeTab === "admin" ? adminRoles.filter(role =>
        role.roleName.toLowerCase().includes(roleSearch.toLowerCase())) : userRoles.filter(role =>
            role.roleName.toLowerCase().includes(roleSearch.toLowerCase())
        );

    const filteredMachines = allDevices.filter(machine =>
        machine.dNSHostName?.toLowerCase().includes(machineSearch.toLowerCase())
    );

    const hasEpamUser = editRoles.includes("epam-user");
    const canSelectOtherRoles = !hasEpamUser;
    const canSaveEdit =
        activeTab === "admin" ? Boolean(editRole?.trim()) : editRoles.length > 0;


    const getRegisteredUser = async () => {
        setLoading(true)
        let userInRoles: UserInRoles;

        if (activeTab === "admin") {
            userInRoles = await getAdminUsers(tenantName);
        } else {
            userInRoles = await getUsers(tenantName);
        }

        setUserInRoles(userInRoles);
    };

    const loadRoles = async () => {
        const roles: Role[] = await fetchAllRoles(tenantName);
        setAdminRoles(roles.filter(role => role.roleType === "admin") || []);
        setUserRoles(roles.filter(role => role.roleType !== "admin").filter(role => role.roleName != "super-user") || [])
    }

    const loadTagsForPicker = async (page: number, search: string) => {
        if (!tenantName) return;
        setIsTagSearchLoading(true);
        try {
            const response = await searchTagsForPicker(tenantName, search, page - 1);
            setAvailableTags(response?.content ?? []);
            //setTagPickerTotalPages(Math.max(response?.totalPages ?? 1, 1));
            //setTagPickerPage(page);
        } catch (error) {
            console.error("Failed to fetch tags:", error);
            setAvailableTags([]);
            //setTagPickerTotalPages(1);
        } finally {
            setIsTagSearchLoading(false);
        }
    };

    useEffect(() => {
        if (!tenantName) return;
        if (skipTabPageResetRef.current) {
            skipTabPageResetRef.current = false;
        } else {
            setCurrentPage(1);
        }
        getRegisteredUser();
        loadRoles();
    }, [activeTab, tenantName])

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.registeredUsers, {
            currentPage,
            searchTerm,
            activeTab,
        });
    }, [currentPage, searchTerm, activeTab]);


    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <UserCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-registered-users-title">
                            Registered Users
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            View and manage all registered users and administrators on the platform
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <Card className="border-border/40">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Registered Users ({filteredUsers.length})
                            </CardTitle>
                            <div className="flex items-center gap-4">
                                <div className="flex bg-muted/50 rounded-lg p-1">
                                    <button
                                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === "admin"
                                            ? "bg-blue-500 text-white shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                            }`}
                                        onClick={() => setActiveTab("admin")}
                                        data-testid="tab-admin-users"
                                    >
                                        <Shield className="w-4 h-4" />
                                        Admin
                                    </button>
                                    <button
                                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === "user"
                                            ? "bg-blue-500 text-white shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                            }`}
                                        onClick={() => setActiveTab("user")}
                                        data-testid="tab-regular-users"
                                    >
                                        <Users className="w-4 h-4" />
                                        Users
                                    </button>
                                </div>
                                <div className="relative w-64">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <Input
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="pl-10"
                                        data-testid="input-search-registered-users"
                                    />
                                </div>
                                <Button
                                    variant={showUnassignedOnly ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => {
                                        setShowUnassignedOnly(!showUnassignedOnly);
                                        setCurrentPage(1);
                                    }}
                                    className="gap-2"
                                    data-testid="button-filter-unassigned"
                                >
                                    <AlertTriangle className="w-4 h-4" />
                                    {showUnassignedOnly ? "Show All" : "Unassigned Only"}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <TabsContent value={activeTab} className="mt-0">

                        <CardContent className="p-0">

                            <div className="border-t">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30">
                                            <TableHead className="font-semibold">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    Registered At
                                                </div>
                                            </TableHead>
                                            <TableHead className="font-semibold">Name</TableHead>
                                            <TableHead className="font-semibold">Email</TableHead>
                                            <TableHead className="font-semibold">Roles</TableHead>
                                            {activeTab === "admin" && <TableHead className="font-semibold">Tags</TableHead>}
                                            {activeTab === "user" && <TableHead className="font-semibold">Machines</TableHead>}
                                            <TableHead className="font-semibold text-center">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                                            <TableRow key={index} className="animate-pulse">
                                                {Array.from({ length: activeTab === "admin" ? 6 : 5 }).map((__, i) => (
                                                    <TableCell key={i}>
                                                        <Skeleton className="h-4 w-full" />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        )) : paginatedUsers?.length > 0 ? paginatedUsers?.map((user, index) => (
                                            <TableRow
                                                key={`${user.email}-${index}`}
                                                className="hover:bg-muted/30 transition-colors"
                                                data-testid={`row-user-${index}`}
                                            >
                                                {/* Registered Date */}
                                                <TableCell data-testid={`registered-at-${index}`}>
                                                    <div className="text-sm text-muted-foreground font-medium">
                                                        {user.createdAt
                                                            ? new Date(user.createdAt).toLocaleDateString()
                                                            : "-"}
                                                    </div>
                                                </TableCell>

                                                {/* Name & Type */}
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium" data-testid={`name-${index}`}>
                                                            {user.firstName} {user.lastName}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground capitalize">
                                                            {user.isFromSSO ? "sso" : ""}
                                                        </span>
                                                    </div>
                                                </TableCell>

                                                <TableCell data-testid={`email-${index}`}>
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-mono text-sm">{user.email}</div>

                                                        {(user.awsUser || user.azureUser || user.gcpUser) && (
                                                            <div className="flex items-center gap-1">
                                                                {user.awsUser && <FaAws className="w-4 h-4 text-orange-500" />}
                                                                {user.azureUser && <VscAzure className="w-4 h-4 text-blue-500" />}
                                                                {user.gcpUser && <FaGoogle className="w-4 h-4 text-red-500" />}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>

                                                {/* Roles */}
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {user.roleName
                                                            .split(",")
                                                            .map((role: string) => role.trim())
                                                            .map((role: string) => (
                                                                <Badge
                                                                    key={role}
                                                                    className={`text-xs ${role === "unassigned-user"
                                                                        ? "bg-red-500 text-white hover:bg-red-600"
                                                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                                                        }`}
                                                                    data-testid={`badge-role-${role}`}
                                                                >
                                                                    {role}
                                                                </Badge>
                                                            ))}
                                                    </div>
                                                </TableCell>


                                                {/* Tags (for admin tab only) */}
                                                {activeTab === "admin" && (
                                                    <TableCell>
                                                        <div className="flex flex-wrap gap-1">
                                                            {user.tagsList && user.tagsList.length > 0 ? (
                                                                user.tagsList.map((tag) => (
                                                                    <Badge
                                                                        key={tag}
                                                                        className="text-xs bg-blue-600 text-white dark:bg-blue-500 dark:text-white border border-blue-600 dark:border-blue-500 gap-1"
                                                                        data-testid={`badge-tag-${tag}`}
                                                                    >
                                                                        <Tags className="w-3 h-3" />
                                                                        {tag}
                                                                    </Badge>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted-foreground text-xs">-</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                )}

                                                {/* Machines (for user tab only) */}
                                                {activeTab === "user" && (
                                                    <TableCell data-testid={`machines-${index}`}>
                                                        {user.adComputerList && user.adComputerList.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1">
                                                                {user.adComputerList.slice(0, 2).map((machine, i) => (
                                                                    <Badge
                                                                        key={machine.id || i}
                                                                        variant="outline"
                                                                        className="text-xs font-mono border border-blue-500 text-blue-700 bg-transparent hover:bg-blue-500/10 dark:border-blue-400 dark:text-blue-200 dark:hover:bg-blue-400/10"
                                                                        data-testid={`badge-machine-${machine.id || i}`}
                                                                    >
                                                                        {machine.dNSHostName || machine.name || machine.id}
                                                                    </Badge>
                                                                ))}
                                                                {user.adComputerList.length > 2 && (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-xs border border-blue-500 text-blue-700 bg-transparent hover:bg-blue-500/10 dark:border-blue-400 dark:text-blue-200 dark:hover:bg-blue-400/10"
                                                                    >
                                                                        +{user.adComputerList.length - 2} more
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground text-xs">-</span>
                                                        )}

                                                    </TableCell>
                                                )}



                                                {/* Actions */}
                                                <TableCell className="text-center" data-testid={`actions-${index}`}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                data-testid={`button-actions-${index}`}
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem
                                                                onClick={() => handleEdit(user)}
                                                                data-testid={`button-edit-${index}`}
                                                            >
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Edit User
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => handleDeleteClick(user)}
                                                                className="text-destructive"
                                                                data-testid={`button-delete-${index}`}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Delete User
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={activeTab === "admin" ? 6 : 5} className="p-6">
                                                    <EmptyStateCard
                                                        embedded
                                                        compact
                                                        icon={UserCheck}
                                                        title={searchTerm ? "No matching users found" : "No registered users"}
                                                        description={
                                                            searchTerm
                                                                ? "Try adjusting your search criteria."
                                                                : "No users have been registered for this tenant yet."
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}

                                    </TableBody>
                                </Table>
                            </div>


                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between p-4 border-t bg-muted/20">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage <= 1}
                                            className="gap-1"
                                            data-testid="button-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </Button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const page = i + 1;
                                                return (
                                                    <Button
                                                        key={page}
                                                        variant={currentPage === page ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(page)}
                                                        data-testid={`button-page-${page}`}
                                                    >
                                                        {page}
                                                    </Button>
                                                );
                                            })}
                                            {totalPages > 5 && (
                                                <>
                                                    <span className="text-muted-foreground px-2">...</span>
                                                    <Button
                                                        variant={currentPage === totalPages ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(totalPages)}
                                                        data-testid={`button-page-${totalPages}`}
                                                    >
                                                        {totalPages}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage >= totalPages}
                                            className="gap-1"
                                            data-testid="button-next-page"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </TabsContent>
                </Tabs>
            </Card>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete{" "}
                            <span className="font-semibold">
                                {userToDelete?.firstName} {userToDelete?.lastName}
                            </span>
                            ? This action cannot be undone and will permanently remove the user from the system.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleDeleteCancel} data-testid="button-cancel-delete">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            Delete User
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Edit User Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5" />
                            Edit
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
                        <div className="space-y-2">
                            <Label htmlFor="userName">User</Label>
                            <div className="p-3 bg-muted rounded-md">
                                <div className="font-medium">
                                    {userToEdit?.firstName} {userToEdit?.lastName}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {userToEdit?.email}
                                </div>
                            </div>
                        </div>

                        {/* Admin User Fields */}
                        {activeTab === "admin" && (
                            <>
                                {/* Role Selection */}
                                <div className="space-y-2">
                                    <Label>Admin Role</Label>
                                    <Select value={editRole} onValueChange={setEditRole}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select admin role..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {adminRoles.map((role) => (
                                                <SelectItem key={role.roleId} value={role.roleName}>
                                                    <div className="flex items-center gap-2">
                                                        <Shield className="w-4 h-4" />
                                                        {role.roleName}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Tag Selection */}
                                <div className="space-y-3">
                                    <Label>Tags</Label>
                                    <Card className="p-3">
                                        <div className="flex flex-wrap gap-2">
                                            {editSelectedTags.map((tag) => (
                                                <Badge
                                                    key={tag.id}
                                                    className="text-xs bg-blue-600 text-white dark:bg-blue-500 dark:text-white border border-blue-600 dark:border-blue-500 gap-1 pr-1"
                                                >
                                                    <Tags className="w-3 h-3" />
                                                    {tag.name}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                        onClick={() => handleTagRemove(tag.id)}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </Badge>
                                            ))}
                                            {editSelectedTags.length === 0 && (
                                                <span className="text-muted-foreground text-sm">No tags assigned</span>
                                            )}
                                        </div>
                                    </Card>
                                    <Select
                                        onValueChange={handleTagSelect}
                                        onOpenChange={(isOpen) => {
                                            if (isOpen) {
                                                loadTagsForPicker(1, tagSearch);
                                            }
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Add tags..." />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-60">
                                            {/* Search Input */}
                                            <div className="p-2 border-b">
                                                <div className="flex items-center gap-2">
                                                    <div className="relative flex-1">
                                                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                        <Input
                                                            placeholder="Search tags..."
                                                            value={tagSearch}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setTagSearch(value);
                                                                if (!value.trim()) {
                                                                    loadTagsForPicker(1, "");
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                e.stopPropagation();
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleTagSearch();
                                                                }
                                                            }}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            className="pl-8 h-8"
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 shrink-0"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleTagSearch();
                                                        }}
                                                        disabled={isTagSearchLoading}
                                                        aria-label="Search tags"
                                                    >
                                                        <Search className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Tag List */}
                                            <div className="max-h-48 overflow-y-auto">
                                                {isTagSearchLoading ? (
                                                    <div className="p-2 text-center text-muted-foreground text-sm">
                                                        Searching tags...
                                                    </div>
                                                ) : filteredTags.length > 0 ? (
                                                    filteredTags.map((tag) => (
                                                        <SelectItem key={tag.id} value={tag.name}>
                                                            <div className="flex items-center gap-2">
                                                                <Tags className="w-4 h-4" />
                                                                {tag.name}
                                                                {editSelectedTags.some((selected) => selected.id === tag.id) && (
                                                                    <Badge variant="secondary" className="ml-auto text-xs">
                                                                        Selected
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <div className="p-2 text-center text-muted-foreground text-sm">
                                                        No tags found
                                                    </div>
                                                )}
                                            </div>

                                            {/**
                                                
                                                tagPickerTotalPages > 1 && (
                                                <div className="flex items-center justify-between gap-2 border-t p-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 px-2"
                                                        disabled={tagPickerPage <= 1}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            loadTagsForPicker(tagPickerPage - 1, tagSearch);
                                                        }}
                                                    >
                                                        Prev
                                                    </Button>
                                                    <span className="text-xs text-muted-foreground">
                                                        Page {tagPickerPage} of {tagPickerTotalPages}
                                                    </span>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 px-2"
                                                        disabled={tagPickerPage >= tagPickerTotalPages}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            loadTagsForPicker(tagPickerPage + 1, tagSearch);
                                                        }}
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            ) */}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}

                        {/* User Type Specific Fields */}
                        {activeTab === "user" && (
                            <div className="space-y-4">
                                {/* Role Selection */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Label>Select Roles</Label>
                                        {hasEpamUser && (
                                            <div className="flex items-center gap-1 text-amber-600 text-xs">
                                                <AlertTriangle className="w-3 h-3" />
                                                epam-user cannot be combined with other roles
                                            </div>
                                        )}
                                    </div>
                                    <Card className="p-3">
                                        <div className="flex flex-wrap gap-2">
                                            {editRoles.length > 0 ? (
                                                editRoles.map((role) => (
                                                    <Badge
                                                        key={role}
                                                        variant="secondary"
                                                        className="gap-1 pr-1"
                                                    >
                                                        {role}
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                            onClick={() => handleRoleSelect(role)}
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </Badge>
                                                ))
                                            ) : (
                                                <span className="text-muted-foreground text-sm">
                                                    No roles selected
                                                </span>
                                            )}
                                        </div>

                                    </Card>
                                    <Select onValueChange={handleRoleSelect}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose roles..." />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-80">
                                            {/* Search Input */}
                                            <div className="p-2 border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                    <Input
                                                        placeholder="Search roles..."
                                                        value={roleSearch}
                                                        onChange={(e) => setRoleSearch(e.target.value)}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        className="pl-8 h-8"
                                                    />
                                                </div>
                                            </div>

                                            {/* Role List */}
                                            <div className="max-h-60 overflow-y-auto">
                                                {filteredRoles.length > 0 ? (
                                                    filteredRoles.filter(role => role.roleName !== "super-user" && role.roleName !== "unassigned-user").map((role) => (
                                                        <SelectItem
                                                            key={role.roleName}
                                                            value={role.roleName}
                                                            disabled={!canSelectOtherRoles && role.roleName !== "epam-user"}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {editRoles.includes(role.roleName) && <CheckCircle className="w-4 h-4 text-primary" />}
                                                                <Users className="w-4 h-4" />
                                                                {role.roleName}
                                                            </div>
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <div className="p-2 text-center text-muted-foreground text-sm">
                                                        No roles found
                                                    </div>
                                                )}
                                            </div>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Machine Selection */}
                                <div className="space-y-3">
                                    <Label>Select Machines</Label>
                                    <Card className="p-3">
                                        <div className="flex flex-wrap gap-2">
                                            {editMachines.map((machine) => (
                                                <Badge
                                                    key={machine}
                                                    variant="secondary"
                                                    className="gap-1 pr-1"
                                                >
                                                    <Monitor className="w-3 h-3" />
                                                    {machine}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                                        onClick={() => handleMachineSelect(machine)}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </Badge>
                                            ))}
                                            {editMachines.length === 0 && (
                                                <span className="text-muted-foreground text-sm">No machines selected</span>
                                            )}
                                        </div>
                                    </Card>
                                    <Select onValueChange={handleMachineSelect}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose machines..." />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-80">
                                            {/* Search Input */}
                                            <div className="p-2 border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                    <Input
                                                        placeholder="Search machines..."
                                                        value={machineSearch}
                                                        onChange={(e) => setMachineSearch(e.target.value)}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        className="pl-8 h-8"
                                                    />
                                                </div>
                                            </div>

                                            {/* Machine List */}
                                            <div className="max-h-60 overflow-y-auto">
                                                {filteredMachines.length > 0 ? (
                                                    filteredMachines.map((machine, index) => (
                                                        <SelectItem key={index} value={machine.dNSHostName ?? ""}>
                                                            <div className="flex items-center gap-2 py-1">
                                                                {editMachines.includes(machine.dNSHostName ?? "") && (
                                                                    <CheckCircle className="w-4 h-4 text-primary" />
                                                                )}
                                                                <Monitor className="w-4 h-4" />
                                                                <span>{machine.dNSHostName ?? "Unknown"}</span>
                                                            </div>
                                                        </SelectItem>

                                                    ))
                                                ) : (
                                                    <div className="p-3 text-center text-muted-foreground text-sm">
                                                        No machines found
                                                    </div>
                                                )}
                                            </div>
                                        </SelectContent>
                                    </Select>
                                    <div className="text-xs text-muted-foreground">
                                        {editMachines.length} of {allDevices.length} machines selected
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleEditCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditSave}
                            disabled={!canSaveEdit}
                        >
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}